import * as ACTIONS from '@/store/constants/actions.js';
import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import { PROCESSING_STATUSES } from '@/constants/moderation.js';
import { TIME_TRACKING_EVENTS } from '@/constants/time.js';
import { HISTORY_CONTENT_ACTIONS } from '@/constants/content.js';
import {
    createContentAssessment, getParentOfContent, getContentActions, createContentAction,
} from '@/generated/console.js';
import {
    pick as livecommentsPick,
    unpick as livecommentsUnpick,
    topCommentsAdd,
    topCommentsExclude,
} from '@/generated/comments.js';
import { pick as liveblogPick, unpick as liveblogUnpick } from '@/generated/blog.js';
import { patchComment } from '@/resources/comment.js';
import {
    MODERATOR, MODERATOR_DECISION, REVIEW_ACTION, CONTENT_ACTIONS,
} from '@/constants/content';
import { SERVICE_V2, SERVICE_LIVECOMMENT, SERVICE_LIVESTORIES } from '@/constants/service';
import { sendAnalyticsBeacon } from '@/resources/rest_api_v4.js';
import { refreshUserTokenRequest } from '@/resources/user';
import { getDomainFromUrl } from '@/util/parser';

const ASSESSMENTS = {
    [PROCESSING_STATUSES.VISIBLE]: 'approved',
    [PROCESSING_STATUSES.DISABLED]: 'rejected',
};

const SOURCE_CONSOLE = 'console';

export const actions = {
    [ACTIONS.SET_FETCHED_CONTENT]({ commit }, item) {
        // Putting this in actions for when we add the media aspect to this as well
        commit(MUTATIONS.SET_CONTENT, {
            key: item.content_id,
            item,
        });
        return Promise.resolve(item.content_id);
    },

    [ACTIONS.FETCH_PARENT]({ getters, commit, dispatch }, { content_id, service, parent_content_uuid }) {
        const content = getters[GETTERS.CONTENT_BY_UUID](parent_content_uuid);

        if (content) {
            return Promise.resolve(content);
        }

        return getParentOfContent({
            content_uuid: content_id,
            parent_content_uuid,
            service,
        }).then((item) => {
            commit(MUTATIONS.SET_CONTENT, {
                key: item.content_id,
                item,
            });

            dispatch(ACTIONS.FETCH_USER_BY_UUID, {
                uuid: item.actor_uuid,
                site_uuid: item.site_uuid,
                origin: item.origin,
            });
        });
    },
    [ACTIONS.PROCESS_CONTENT_ACTION]({ commit, dispatch }, {
        content_id, queue_uuid, status, service, tags, section_uuid,
    }) {
        const content_assessment = {
            content_id,
            section_uuid,
            service,
            assessment: ASSESSMENTS[status],
            tags: tags.map((tag) => {
                // don't send the moderator uuids. Backend already has it and can figure out who the current moderator is
                delete tag.moderator_id;

                // TODO we can get rid of this later, but let's stop sending confidence to the backend to slowly unpollute the data
                delete tag.confidence;
                return tag;
            }),
        };
        return createContentAssessment({ content_assessment }).then(() => {
            commit(MUTATIONS.ADD_EVENT_TO_HISTORY, TIME_TRACKING_EVENTS.content_action(content_id, ASSESSMENTS[status]));
            commit(MUTATIONS.CHANGE_PROCESSING_STATUS, {
                content_id,
                status,
            });

            // After a content assessment, reset the tag_changed attribute
            commit(MUTATIONS.UPDATE_CONTENT_TAG_CHANGED, {
                content_id,
                status: false,
            });

            // If its a queue, remove the content from the queue
            if (queue_uuid) {
                commit(MUTATIONS.REMOVE_CONTENT_FROM_QUEUE, {
                    content_id,
                    queue_uuid,
                });
            }

            // Push to content history
            dispatch(ACTIONS.ADD_CONTENT_ACTION, {
                content_id,
                action: MODERATOR_DECISION,
                value: ASSESSMENTS[status],
            });
        });
    },
    [ACTIONS.FETCH_CONTENT_HISTORY]({ commit }, { content_id, service }) {
        return getContentActions({
            content_uuid: content_id,
            service,
        }).then(({ actions }) => {
            commit(MUTATIONS.SET_CONTENT, {
                key: content_id,
                item: {
                    actions,
                },
            });
            return Promise.resolve(actions);
        });
    },

    [ACTIONS.REVIEW_CONTENT]({ commit, dispatch, getters }, { content_id, service, action_source }) {
        return createContentAction({
            content_action: {
                content_id,
                service,
                action: MODERATOR_DECISION,
                performed_by: MODERATOR,
                value: REVIEW_ACTION,
                source: SOURCE_CONSOLE,
            },
        })
            .then(() => {
                const queue_uuid = getters[GETTERS.CURRENT_QUEUE_UUID];
                commit(MUTATIONS.ADD_EVENT_TO_HISTORY, TIME_TRACKING_EVENTS.content_action(content_id, 'reviewed'));

                if (queue_uuid) {
                    commit(MUTATIONS.REMOVE_CONTENT_FROM_QUEUE, {
                        content_id,
                        queue_uuid,
                    });
                }

                // Push to content history
                dispatch(ACTIONS.ADD_CONTENT_ACTION, {
                    content_id,
                    action: MODERATOR_DECISION,
                    value: REVIEW_ACTION,
                });

                dispatch(ACTIONS.SEND_ANALYTICS_CONTENT_EVENT, {
                    content_id,
                    action: REVIEW_ACTION,
                    action_source,
                });
            });
    },

    [ACTIONS.SUBMIT_EDITOR_PICK]({ commit, dispatch }, {
        content, action,
    }) {
        // We are going to set the editor's pick from the frontend so we need to figure out which endpoint to hit
        return new Promise((resolve, reject) => {
            const siteDomain = getDomainFromUrl(content.raw_page_url);
            refreshUserTokenRequest(siteDomain).then(() => {
                switch (content.service) {
                    case SERVICE_V2:
                        patchComment(content.content_id, content.site_uuid, {
                            editor_pick: action === CONTENT_ACTIONS.EDITOR_PICK,
                        }).then(resolve);
                        break;
                    case SERVICE_LIVESTORIES:
                        const liveblogMethod = action === CONTENT_ACTIONS.EDITOR_PICK ? liveblogPick : liveblogUnpick;

                        liveblogMethod({
                            content_uuid: content.content_id,
                            content_container_uuid: content.container_uuid,
                            section_uuid: content.site_uuid,
                        }).then(resolve);
                        break;
                    case SERVICE_LIVECOMMENT:
                        const livecommentsMethod = action === CONTENT_ACTIONS.EDITOR_PICK ? livecommentsPick : livecommentsUnpick;
                        livecommentsMethod({
                            content_uuid: content.content_id,
                            content_container_uuid: content.container_uuid,
                            section_uuid: content.site_uuid,
                        }).then(resolve);
                        break;
                    default:
                        reject();
                }
            }).catch(() => {
                // eslint-disable-next-line no-console
                console.error('We could not refresh the access token');
            });
        }).then(() => {
            commit(MUTATIONS.SET_CONTENT, {
                key: content.content_id,
                item: {
                    editor_pick: action === CONTENT_ACTIONS.EDITOR_PICK,
                },
            });

            // Push to content history
            dispatch(ACTIONS.ADD_CONTENT_ACTION, {
                content_id: content.content_id,
                action,
            });
        });
    },

    [ACTIONS.TOP_COMMENTS_EXCLUDE]({ commit, dispatch }, { content }) {
        if (content.service !== SERVICE_LIVECOMMENT) {
            return Promise.reject('Invalid service');
        }

        const siteDomain = getDomainFromUrl(content.raw_page_url);

        return refreshUserTokenRequest(siteDomain)
            .then(() => topCommentsExclude({
                content_uuid: content.content_id,
                content_container_uuid: content.container_uuid,
                section_uuid: content.section_uuid,
            }))
            .then(() => {
                commit(MUTATIONS.SET_CONTENT, {
                    key: content.content_id,
                    item: {
                        is_top_comment: false,
                        is_top_comment_eligible_by_mod: false,
                    },
                });

                return dispatch(ACTIONS.ADD_CONTENT_ACTION, {
                    content_id: content.content_id,
                    action: HISTORY_CONTENT_ACTIONS.TOP_COMMENTS_EXCLUDED,
                });
            });
    },

    [ACTIONS.TOP_COMMENTS_REINCLUDE]({ commit, dispatch }, { content }) {
        if (content.service !== SERVICE_LIVECOMMENT) {
            return Promise.reject('Invalid service');
        }

        const siteDomain = getDomainFromUrl(content.raw_page_url);

        return refreshUserTokenRequest(siteDomain)
            .then(() => topCommentsAdd({
                content_uuid: content.content_id,
                content_container_uuid: content.container_uuid,
                section_uuid: content.section_uuid,
            }))
            .then(() => {
                commit(MUTATIONS.SET_CONTENT, {
                    key: content.content_id,
                    item: {
                        is_top_comment: true,
                        is_top_comment_eligible_by_mod: true,
                    },
                });

                return dispatch(ACTIONS.ADD_CONTENT_ACTION, {
                    content_id: content.content_id,
                    action: HISTORY_CONTENT_ACTIONS.TOP_COMMENTS_REINCLUDED,
                });
            });
    },

    [ACTIONS.ADD_CONTENT_ACTION]({ commit }, { content_id, action, value }) {
        commit(MUTATIONS.ADD_CONTENT_ACTION, {
            date_created: Date.now(),
            content_id,
            action,
            value,
            performed_by: 'moderator',
            actor_uuid: GETTERS.CURRENT_USER_UUID,
        });
    },

    [ACTIONS.SEND_ANALYTICS_CONTENT_EVENT]({ getters }, { content_id, action, action_source }) {
        const workspace_uuid = getters[GETTERS.CURRENT_WORKSPACE_UUID];
        const queue_uuid = getters[GETTERS.CURRENT_QUEUE_UUID];
        const saved_search_uuid = getters[GETTERS.CURRENT_SEARCH_UUID];
        const site_uuid = getters[GETTERS.ACTIVE_SITE_UUID];

        sendAnalyticsBeacon({
            action,
            action_source,
            workspace_uuid,
            queue_uuid,
            site_uuid,
            saved_search_uuid,
            content_id,
            console_version: getters[GETTERS.CONSOLE_VERSION],
        });
    },
};
